import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { Reveal, LineReveal } from "../components/Reveal";
import { site } from "../content/site";

const pages = {
  privacy: {
    title: "Privacy Policy",
    path: "/privacy",
    sections: [
      {
        heading: "What we collect",
        paragraphs: [
          "When you write to us through the Get Involved form, we collect the information you choose to share: your name, email address, your enquiry type, your organisation (where provided), and your message. We collect nothing else about you directly.",
        ],
      },
      {
        heading: "How we use it",
        paragraphs: [
          "We use this information only to respond to you and to act on the reason you contacted us — enquiries, participation, volunteering, collaboration, and the operation of our website and community. We do not sell, rent or trade your information, and we do not use it for marketing lists you didn't ask to join.",
        ],
      },
      {
        heading: "Technical data, cookies and analytics",
        paragraphs: [
          "Like most websites, our hosting infrastructure keeps standard technical logs (such as browser type and pages visited) for security and performance. We use privacy-conscious, aggregate analytics to understand how the site is used; we do not build individual profiles. We do not use advertising cookies or trackers.",
        ],
      },
      {
        heading: "Third-party services",
        paragraphs: [
          "Enquiry emails are delivered using Resend, which processes your name, email and message solely to deliver that email. Our website links to external platforms — including Pacer, WhatsApp and Instagram — which have their own privacy practices when you choose to use them.",
        ],
      },
      {
        heading: "Security and retention",
        paragraphs: [
          "We take reasonable measures to protect the information you share and keep enquiries only for as long as they are needed to handle your request and our community operations. You may ask us at any time to view or delete your enquiry.",
        ],
      },
      {
        heading: "Children's privacy",
        paragraphs: [
          "This website is intended for a general audience and is not directed at children. We do not knowingly collect personal information from children through this website.",
        ],
      },
      {
        heading: "Updates and questions",
        paragraphs: [
          `If this policy changes, the revised version will be posted here. For any privacy question, correction or deletion request, write to us at ${site.email}.`,
        ],
      },
    ],
  },
  terms: {
    title: "Terms of Use",
    path: "/terms",
    sections: [
      {
        heading: "About this website",
        paragraphs: [
          "This website shares information about The PilCoo Foundation — its activities, events, stories and ways to participate. By using it, you accept these terms.",
        ],
      },
      {
        heading: "Information, accuracy and availability",
        paragraphs: [
          "We work to keep information accurate and current, but the website is provided as-is: details of activities and events may change, and we don't guarantee uninterrupted availability. Where something is planned rather than operational, we say so.",
        ],
      },
      {
        heading: "Activities, events and community participation",
        paragraphs: [
          "Participation in PilCoo activities and events is voluntary and at your own discretion and risk. Some take place on external platforms (such as Pacer), which have their own terms and privacy practices. Please participate within your own limits and local rules.",
        ],
      },
      {
        heading: "Mental wellbeing content",
        paragraphs: [
          "Our mental-wellbeing content is offered for awareness, education and community support only. It is not medical advice, diagnosis, psychotherapy or crisis intervention. The PilCoo Foundation is not a healthcare provider, therapy provider or crisis service. If you need urgent help, please contact a qualified professional or local emergency service.",
        ],
      },
      {
        heading: "Animal welfare content",
        paragraphs: [
          "Submitting an enquiry about an animal does not guarantee rescue, veterinary intervention or emergency response. We are a connector and facilitator — we do our best to route genuine needs to the right people, but we cannot promise outcomes.",
        ],
      },
      {
        heading: "External links",
        paragraphs: [
          "We link to third-party platforms including Pacer, WhatsApp and Instagram. We don't control and aren't responsible for their content, terms or privacy practices.",
        ],
      },
      {
        heading: "Intellectual property and acceptable use",
        paragraphs: [
          "The PilCoo Foundation name, logo and website content belong to The PilCoo Foundation; please don't reuse them in ways that suggest endorsement or affiliation without permission. Use this website lawfully and respectfully — don't misuse the enquiry form or misrepresent yourself.",
        ],
      },
      {
        heading: "Limitation of liability",
        paragraphs: [
          "We work hard to keep information on this website accurate and helpful, but it's provided in good faith and without guarantees. To the extent permitted by law, The PilCoo Foundation isn't liable for decisions made based on this website's content or for the services of third parties we connect you with.",
        ],
      },
      {
        heading: "Changes and contact",
        paragraphs: [
          `We may update these terms as the website evolves; the current version always lives here. Questions: ${site.email}.`,
        ],
      },
    ],
  },
  transparency: {
    title: "Transparency",
    path: "/transparency",
    sections: [
      {
        heading: "Kindness deserves accountability",
        paragraphs: [
          "The PilCoo Foundation is at the beginning of its work. We don't have a long operational history to publish yet — and we think the honest thing is to say exactly that, and to show you the standard we'll hold ourselves to as we grow.",
        ],
      },
      {
        heading: "The model we'll report by",
        paragraphs: [
          "Every initiative follows the same open loop: Need identified → Action defined → Resources directed → Action completed → Outcome documented → Impact shared. A clear line of sight from every contribution to the care or action it enables.",
          "If we can't document it, we won't publish it. No estimates dressed up as impact, no numbers without evidence behind them.",
        ],
      },
      {
        heading: "How funding is intended to work",
        paragraphs: [
          "PilCoo is currently supported by its founders and by businesses they run: Retrove, a merchandise venture whose profits are intended to support the foundation's work; The Flavor Bag, which contributes a share of business profits; and Hoppbugs, which provides technology support. This website does not accept online donations.",
          "Programme spending follows a simple philosophy: resources go to verified needs, through the people and organisations best placed to meet them, with outcomes documented. No financial figures are published until there are real, evidenced figures to publish.",
        ],
      },
      {
        heading: "Where things stand today",
        paragraphs: [
          "PilCoo is an early-stage compassion-led community initiative. We do not currently claim charitable registration, tax-exemption status or institutional certifications we don't hold. Formal organisational registrations — and the reporting that comes with them — will be reflected here as milestones are reached.",
          "Our first documented outcome is live: the community's progress in 1 Lakh Steps for Paws.",
        ],
      },
      {
        heading: "See it in practice",
        paragraphs: [
          "Our Impact page carries every verified outcome as it happens.",
        ],
        link: { to: "/impact", label: "See our Impact" },
      },
      {
        heading: "Questions",
        paragraphs: [
          `Transparency works both ways — if something on this site is unclear or you want to know more, write to us at ${site.email}.`,
        ],
      },
    ],
  },
};

export default function Legal({ page }) {
  const content = pages[page] || pages.privacy;
  return (
    <main>
      <SEO title={content.title} description={`${content.title} — The PilCoo Foundation`} path={content.path} />
      <section className="pt-32 md:pt-44 pb-24 md:pb-32" data-testid={`legal-${page}`}>
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <p className="text-xs uppercase tracking-[0.25em] font-medium text-ink-2">The PilCoo Foundation</p>
          <h1 className="mt-6 font-serif tracking-tighter leading-[0.98] text-ink text-5xl sm:text-6xl">
            <LineReveal lines={[content.title]} delay={0.15} />
          </h1>
          <Reveal delay={0.35}>
            <p className="mt-6 text-xs uppercase tracking-[0.18em] text-ink-2">Last updated: August 2026</p>
          </Reveal>
          <Reveal delay={0.45}>
            <div className="mt-12 space-y-12">
              {content.sections.map((section) => (
                <section key={section.heading}>
                  <h2 className="font-serif text-2xl md:text-3xl tracking-tight text-ink">{section.heading}</h2>
                  {section.paragraphs.map((p, i) => (
                    <p key={i} className="mt-4 text-sm md:text-base leading-relaxed text-ink-2">{p}</p>
                  ))}
                  {section.link && (
                    <Link
                      to={section.link.to}
                      data-testid="legal-impact-link"
                      className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-action underline underline-offset-4 hover:text-ink"
                    >
                      {section.link.label} →
                    </Link>
                  )}
                </section>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
